import type { Workspace } from "@gtm/contracts/core/identity";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { ErrorBoundary } from "@/app/error-boundary";
import { setDataInvalidator, usePathname, useSearchParams } from "@/app/navigation";
import { NotFound } from "@/app/not-found";
import {
  CompaniesPage,
  ConsolePage,
  ExclusionsPage,
  IntegrationsPage,
  KnowledgePage,
  MembersPage,
  ObservabilityPage,
  PeoplePage,
  PreferencesPage,
  QaPage,
  SignalsPage,
  WorkflowsPage,
} from "@/app/pages";
import { LoadingLayout } from "@/components/loading-layout";
import { Shell } from "@/components/shell";
import { NoWorkspace, Unavailable } from "@/features/authentication/app-states";
import { InvitationView } from "@/features/authentication/invitation-view";
import { SignIn } from "@/features/authentication/sign-in";
import { DreamListView } from "@/features/dream-list/dream-list-view";
import { ProductRequestsView } from "@/features/knowledge/product-requests-view";
import { TagsView } from "@/features/tags/tags-view";

interface SessionResponse {
  user: { id: string; name: string } | null;
  workspaces: Workspace[];
  /** `dev` means the local identity provider; the API refuses it in production. */
  authMode?: "dev" | "oidc";
  /** Present when verified at the provider but a member of no workspace. */
  authenticatedAs?: string | null;
}

/**
 * Application login state.
 *
 * The identity provider answers whether a credential is valid; this answers
 * what the application should show, which is a different question. A verified
 * identity with no membership is its own state, not an authentication failure,
 * and it gets its own screen.
 */
export function Root() {
  const queryClient = useQueryClient();
  const pathname = usePathname();

  // Lets router.refresh() mean what it meant before: get the data again.
  useEffect(() => {
    setDataInvalidator(() => void queryClient.invalidateQueries());
  }, [queryClient]);

  useEffect(() => {
    const refreshSession = () => void queryClient.invalidateQueries({ queryKey: ["session"] });
    window.addEventListener("session-expired", refreshSession);
    return () => window.removeEventListener("session-expired", refreshSession);
  }, [queryClient]);

  // An invitation is reachable without a session: the invitee has no
  // membership yet, and the page itself explains what they are joining.
  const invite = /^\/invite\/([^/?]+)/.exec(window.location.pathname)?.[1];

  const session = useQuery({
    queryKey: ["session"],
    retry: false,
    queryFn: async (): Promise<SessionResponse> => {
      const response = await fetch("/api/auth/session", { credentials: "include" });
      if (response.status === 401) return (await response.json()) as SessionResponse;
      if (!response.ok) throw new Error("The service is unavailable.");
      return (await response.json()) as SessionResponse;
    },
  });

  if (invite) return <InvitationView token={invite} />;

  if (session.isPending) return <LoadingLayout message="Loading workspace…" />;
  if (session.isError) return <Unavailable message={session.error.message} />;
  // Signed in and a member of nothing is its own state, and showing the
  // sign-in screen for it is actively misleading: it tells somebody who has
  // just signed in successfully that they have not, and invites them to do the
  // one thing that will not help.
  if (!session.data.user) {
    return session.data.authenticatedAs ? (
      <NoWorkspace name={session.data.authenticatedAs} />
    ) : (
      <SignIn authMode={session.data.authMode ?? "oidc"} />
    );
  }
  if (session.data.workspaces.length === 0) return <NoWorkspace name={session.data.user.name} />;

  // A workspace-less URL lands on the first workspace the person belongs to.
  const workspaceId = /^\/w\/([^/]+)/.exec(window.location.pathname)?.[1];
  const workspace =
    session.data.workspaces.find((candidate) => candidate.id === workspaceId) ??
    session.data.workspaces[0];

  // Login is an entry point, not a workspace page. This also recovers old
  // callbacks and bookmarks pointing at /w/:workspaceId/login.
  const isLogin = pathname === "/login" || pathname === "/login/";
  if (!workspaceId || workspaceId !== workspace.id || isLogin) {
    window.history.replaceState(
      null,
      "",
      isLogin
        ? `/w/${workspace.id}`
        : `/w/${workspace.id}${pathname === "/" ? "" : pathname}${window.location.search}${window.location.hash}`,
    );
  }

  return (
    <Shell
      key={workspace.id}
      workspaceId={workspace.id}
      workspaces={session.data.workspaces}
      user={session.data.user}
    >
      {/*
        Inside the shell, so a screen that fails to render leaves the
        navigation intact and somebody can go elsewhere rather than reload.
        Keyed by location so moving away clears the failure.
      */}
      <ErrorBoundary resetKey={pathname + window.location.search}>
        <Route />
      </ErrorBoundary>
    </Shell>
  );
}

/** Routes, matched against the path with the workspace prefix removed. */
function Route() {
  const pathname = usePathname();
  useSearchParams();

  switch (pathname) {
    case "/":
      return <SignalsPage />;
    case "/people":
      return <PeoplePage />;
    case "/companies":
      return <CompaniesPage />;
    case "/workflows":
      return <WorkflowsPage />;
    case "/integrations":
      return <IntegrationsPage />;
    case "/exclusions":
      return <ExclusionsPage />;
    case "/tags":
      return <TagsView />;
    case "/knowledge":
      return <KnowledgePage />;
    case "/dream-list":
      return <DreamListView />;
    case "/product-requests":
      return <ProductRequestsView />;
    case "/qa":
      return <QaPage />;
    case "/observability":
      return <ObservabilityPage />;
    case "/query":
      return <ConsolePage />;
    case "/settings/members":
      return <MembersPage />;
    case "/settings/preferences":
      return <PreferencesPage />;
    default:
      // Not the signals board. Rendering a real page for an address that does
      // not exist hides the mistake and leaves the wrong URL in the bar.
      return <NotFound pathname={pathname} />;
  }
}
